import type {
  DocumentStats,
  PMNode as PMNodeJSON,
  ProseMirrorDoc,
  SceneStats,
  ScriptBlockInput,
  ScreenplayStyle,
} from "../types.js";
import { newBlockId } from "../ids.js";
import { paragraphJSON } from "./schema.js";

export const DIALOGUE_STYLES = new Set<ScreenplayStyle>(["dialogue", "lyrics", "parenthetical"]);
export const SPOKEN_STYLES = new Set<ScreenplayStyle>(["dialogue", "lyrics"]);
export const HEADING_STYLES = new Set<ScreenplayStyle>([
  "sceneHeading",
  "actHeading",
  "sequenceHeading",
  "beatHeading",
]);

/**
 * Structural node type shared by ProseMirror nodes and plain JSON documents.
 * The core utilities (stats, Fountain import/export) must work in both the
 * browser and the local backend, so they only rely on this subset.
 */
export interface ScriptNodeLike {
  type: string | { name: string };
  attrs?: Record<string, unknown> | null;
  text?: string | null;
  content?: ScriptNodeLike[] | { forEach: (fn: (node: ScriptNodeLike, offset?: number, index?: number) => void) => void };
}

export function nodeTypeName(node: ScriptNodeLike): string {
  return typeof node.type === "string" ? node.type : node.type.name;
}

export function nodeChildren(node: ScriptNodeLike): ScriptNodeLike[] {
  if (!node.content) return [];
  if (Array.isArray(node.content)) return node.content;
  const children: ScriptNodeLike[] = [];
  node.content.forEach((child) => children.push(child));
  return children;
}

export function nodeText(node: ScriptNodeLike | undefined | null): string {
  if (!node) return "";
  if (node.text) return node.text;
  if (!node.content) return "";
  return nodeChildren(node).map(nodeText).join("");
}

export function blockText(node: ScriptNodeLike): string {
  return nodeText(node).replace(/\u2028/g, " ").trim();
}

export interface WalkedBlock {
  node: ScriptNodeLike;
  index: number;
  path: number[];
  column?: "left" | "right";
  dualId?: string;
}

/** Flat list of paragraphs in document order; dual-dialogue columns included. */
export function walkBlocks(doc: ProseMirrorDoc): WalkedBlock[] {
  const out: WalkedBlock[] = [];
  const walk = (nodes: ScriptNodeLike[], path: number[], column?: "left" | "right", dualId?: string) => {
    nodes.forEach((node, i) => {
      const p = [...path, i];
      const type = nodeTypeName(node);
      if (type === "paragraph" || type === "pageBreak") {
        out.push({ node, index: out.length, path: p, column, dualId });
      } else if (type === "dual") {
        nodeChildren(node).forEach((col, ci) => {
          walk(
            nodeChildren(col),
            [...p, ci],
            ci === 0 ? "left" : "right",
            (node.attrs?.id as string) ?? undefined,
          );
        });
      }
    });
  };
  walk(doc.content ?? [], []);
  return out;
}

export function hasContent(node: ScriptNodeLike): boolean {
  return blockText(node).length > 0;
}

export function getBlockStyle(node: ScriptNodeLike): ScreenplayStyle {
  return (node.attrs?.style as ScreenplayStyle) ?? "action";
}

export function getBlockId(node: ScriptNodeLike): string {
  return (node.attrs?.id as string) ?? "";
}

export function countWords(text: string): number {
  const normalized = text.replace(/[\u2018\u2019]/g, "'").trim();
  if (!normalized) return 0;
  return (normalized.match(/[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu) ?? []).length;
}

export function countCharacters(text: string): { withSpaces: number; withoutSpaces: number } {
  return {
    withSpaces: Array.from(text).length,
    withoutSpaces: Array.from(text.replace(/\s/g, "")).length,
  };
}

/** Speaker cue without (V.O.), (CONT'D), etc. */
export function normalizeSpeakerCue(cue: string): string {
  return cue
    .replace(/\([^)]*\)/g, "")
    .replace(/\s+/g, " ")
    .replace(/[.:]+$/g, "")
    .trim()
    .toUpperCase();
}

export interface SceneHeadingInfo {
  blockId: string;
  heading: string;
  index: number;
  sceneNumber: string | null;
}

export function getSceneHeadings(doc: ProseMirrorDoc): SceneHeadingInfo[] {
  const blocks = walkBlocks(doc);
  const out: SceneHeadingInfo[] = [];
  blocks.forEach((block) => {
    if (getBlockStyle(block.node) === "sceneHeading") {
      out.push({
        blockId: getBlockId(block.node),
        heading: blockText(block.node),
        index: out.length,
        sceneNumber: (block.node.attrs?.sceneNumber as string | null) ?? null,
      });
    }
  });
  return out;
}

export interface SceneHeadingParts {
  prefix: string;
  location: string;
  timeOfDay: string;
}

/**
 * Parses both Russian and English scene headings without guessing from the
 * bible. The result is display-oriented: "ИНТ. СТУДИЯ — НОЧЬ" becomes
 * { prefix: "ИНТ.", location: "СТУДИЯ", timeOfDay: "НОЧЬ" }. Headings without
 * a time suffix keep "ДЕНЬ" as the neutral default.
 */
export function parseSceneHeading(heading: string | null | undefined): SceneHeadingParts {
  const text = (heading ?? "").replace(/\s+/g, " ").trim();
  const prefixMatch = text.match(/^(ИНТ\.?\s*\/\s*НАТ\.?|ИНТ\.?|НАТ\.?|INT\.?\s*\/\s*EXT\.?|INT\.?|EXT\.?)\s*/i);
  const prefix = (prefixMatch?.[1] ?? "ИНТ.").replace(/\s+/g, "").toUpperCase();
  const rest = text.slice(prefixMatch?.[0].length ?? 0).trim();
  if (!rest) return { prefix, location: "ЛОКАЦИЯ", timeOfDay: "ДЕНЬ" };

  const parts = rest.split(/\s+[—–-]\s+/);
  if (parts.length >= 2) {
    const timeOfDay = parts.pop()!.trim().toUpperCase();
    const location = parts.join(" — ").trim();
    return {
      prefix,
      location: location || "ЛОКАЦИЯ",
      timeOfDay: timeOfDay || "ДЕНЬ",
    };
  }

  return { prefix, location: rest, timeOfDay: "ДЕНЬ" };
}

const CHARS_PER_LINE: Record<ScreenplayStyle, number> = {
  sceneHeading: 60,
  sceneCharacters: 50,
  action: 60,
  character: 36,
  parenthetical: 26,
  dialogue: 35,
  lyrics: 35,
  transition: 50,
  shot: 60,
  inlineNote: 55,
  sequenceHeading: 55,
  actHeading: 55,
  beatHeading: 55,
  unformattedText: 60,
  pageBreak: 0,
};

const SPACING_BEFORE: Partial<Record<ScreenplayStyle, number>> = {
  sceneHeading: 2,
  action: 0.6,
  character: 1,
  transition: 1,
  actHeading: 3,
  sequenceHeading: 2,
  shot: 1,
};

const SPACING_AFTER: Partial<Record<ScreenplayStyle, number>> = {
  sceneHeading: 0.7,
  dialogue: 0.5,
  transition: 0.7,
};

export const LINES_PER_PAGE = 55;

export function blockLineCount(node: ScriptNodeLike): number {
  const style = getBlockStyle(node);
  if (nodeTypeName(node) === "pageBreak") return 0;
  const text = blockText(node);
  const cpl = CHARS_PER_LINE[style] || 55;
  const lines = Math.max(1, Math.ceil(text.length / cpl));
  return (
    lines + (SPACING_BEFORE[style] ?? 0) + (SPACING_AFTER[style] ?? 0)
  );
}

export function analyzeDocument(
  doc: ProseMirrorDoc,
  options: { titlePage?: boolean; linesPerPage?: number } = {},
): DocumentStats {
  const blocks = walkBlocks(doc);
  const styles: Record<string, { blocks: number; words: number }> = {};
  let words = 0;
  let characters = 0;
  let charactersNoSpaces = 0;
  let dialogueWords = 0;
  let actionWords = 0;
  let pageBreakCount = 0;
  let lineCursor = 0;

  // If the first block is a title page marker we don't need special handling:
  // title pages are stored separately.

  const sceneMap = new Map<
    string,
    {
      blockId: string;
      heading: string;
      words: number;
      dialogueWords: number;
      characters: Set<string>;
      pageStart: number;
      sceneNumber: number;
      index: number;
    }
  >();
  const characterMap = new Map<
    string,
    { words: number; lines: number; scenes: Set<string> }
  >();

  let currentSceneId: string | null = null;
  let sceneCounter = 0;
  let currentSpeaker: string | null = null;
  let sceneCharacters: string[] = [];

  for (const block of blocks) {
    const node = block.node;
    if (nodeTypeName(node) === "pageBreak") {
      pageBreakCount += 1;
      const inPage = lineCursor % LINES_PER_PAGE;
      if (inPage !== 0) lineCursor += LINES_PER_PAGE - inPage;
      continue;
    }
    const style = getBlockStyle(node);
    const text = blockText(node);
    const blockWords = countWords(text);
    const chars = countCharacters(text);

    words += blockWords;
    characters += chars.withSpaces;
    charactersNoSpaces += chars.withoutSpaces;
    const entry = (styles[style] ??= { blocks: 0, words: 0 });
    entry.blocks += 1;
    entry.words += blockWords;

    if (SPOKEN_STYLES.has(style)) dialogueWords += blockWords;
    else if (style !== "character" && style !== "parenthetical" && style !== "inlineNote")
      actionWords += blockWords;

    const pageStart = Math.floor(lineCursor / LINES_PER_PAGE) + 1;
    lineCursor += blockLineCount(node);

    if (style === "sceneHeading") {
      sceneCounter += 1;
      currentSceneId = getBlockId(node) || `scene-${sceneCounter}`;
      sceneCharacters = [];
      currentSpeaker = null;
      sceneMap.set(currentSceneId, {
        blockId: currentSceneId,
        heading: text || `Сцена ${sceneCounter}`,
        words: 0,
        dialogueWords: 0,
        characters: new Set(),
        pageStart,
        sceneNumber: sceneCounter,
        index: sceneCounter,
      });
    } else if (style === "sceneCharacters") {
      sceneCharacters = String(text)
        .split(/[,;]/)
        .map((s) => normalizeSpeakerCue(s))
        .filter(Boolean);
      if (currentSceneId) {
        const scene = sceneMap.get(currentSceneId);
        sceneCharacters.forEach((name) => scene?.characters.add(name));
      }
    } else if (style === "character") {
      currentSpeaker = normalizeSpeakerCue(text) || null;
    } else if (SPOKEN_STYLES.has(style)) {
      const speaker = currentSpeaker ?? "БЕЗ ПОДПИСИ";
      if (currentSceneId) {
        const scene = sceneMap.get(currentSceneId);
        scene?.characters.add(speaker);
        if (scene) scene.dialogueWords += blockWords;
      }
      const stat = characterMap.get(speaker) ?? {
        words: 0,
        lines: 0,
        scenes: new Set<string>(),
      };
      stat.words += blockWords;
      stat.lines += 1;
      if (currentSceneId) stat.scenes.add(currentSceneId);
      characterMap.set(speaker, stat);
    }

    if (currentSceneId) {
      const scene = sceneMap.get(currentSceneId);
      if (scene) scene.words += blockWords;
    }
  }

  const linesPerPage = options.linesPerPage ?? LINES_PER_PAGE;
  const pages =
    lineCursor === 0 && pageBreakCount === 0
      ? 1
      : Math.max(1, Math.ceil(lineCursor / linesPerPage) + 0);
  const scenesList: SceneStats[] = Array.from(sceneMap.values()).map((s) => ({
    blockId: s.blockId,
    number: s.sceneNumber,
    heading: s.heading,
    words: s.words,
    dialogueWords: s.dialogueWords,
    characters: Array.from(s.characters),
    pageStart: s.pageStart,
    estimatedSeconds: Math.max(15, Math.round((s.dialogueWords / 140) * 60 + ((s.words - s.dialogueWords) / 80) * 60)),
    style: "sceneHeading",
  }));

  const charactersStats = Array.from(characterMap.entries())
    .map(([name, s]) => ({
      name,
      words: s.words,
      lines: s.lines,
      scenes: s.scenes.size,
    }))
    .sort((a, b) => b.words - a.words);

  return {
    words,
    characters,
    charactersNoSpaces,
    pages,
    blocks: blocks.length,
    scenes: sceneCounter,
    dialogueWords,
    actionWords,
    speakingCharacters: charactersStats.length,
    estimatedSeconds: Math.max(
      30,
      Math.round(scenesList.reduce((acc, s) => acc + s.estimatedSeconds, 0)),
    ),
    readingMinutes: Math.max(1, Math.round(words / 200)),
    styles,
    charactersStats,
    scenesList,
    titlePage: options.titlePage ?? false,
  };
}

export function docToPlainText(doc: ProseMirrorDoc, includeNotes = false): string {
  return walkBlocks(doc)
    .filter((b) => includeNotes || getBlockStyle(b.node) !== "inlineNote")
    .map((b) => {
      const style = getBlockStyle(b.node);
      const text = blockText(b.node);
      if (style === "transition") return text.startsWith(">") ? text : `> ${text}`;
      return text;
    })
    .join("\n\n");
}

export function docToScriptBlocks(doc: ProseMirrorDoc): ScriptBlockInput[] {
  return walkBlocks(doc).map((b) => ({
    id: getBlockId(b.node),
    style: getBlockStyle(b.node),
    text: blockText(b.node),
    revision: (b.node.attrs?.revision as ScriptBlockInput["revision"]) ?? "none",
    sceneNumber: (b.node.attrs?.sceneNumber as string | null) ?? null,
  }));
}

export function blocksToDoc(blocks: ScriptBlockInput[]): ProseMirrorDoc {
  return {
    type: "doc",
    content: blocks.map((b) =>
      b.style === "pageBreak"
        ? { type: "pageBreak", attrs: { id: b.id ?? newBlockId() } }
        : paragraphJSON(b.style, b.text, {
            id: b.id,
            revision: b.revision,
            sceneNumber: b.sceneNumber,
          }),
    ),
  };
}

export function textToDoc(text: string): ProseMirrorDoc {
  const blocks = text
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => ({ style: "action" as ScreenplayStyle, text: chunk }));
  return blocksToDoc(blocks.length ? blocks : [{ style: "action", text: "" }]);
}

/** We attach scene numbers as a stable display attribute on headings. */
export function withSceneNumbers(doc: ProseMirrorDoc): ProseMirrorDoc {
  let n = 0;
  const next = (node: PMNodeJSON): PMNodeJSON => {
    if (node.type === "paragraph" && getBlockStyle(node) === "sceneHeading") {
      n += 1;
      return {
        ...node,
        attrs: { ...node.attrs, sceneNumber: String(n) },
      };
    }
    if (node.content) return { ...node, content: node.content.map(next) };
    return node;
  };
  return { ...doc, content: (doc.content ?? []).map(next) };
}

export function findBlock(doc: ProseMirrorDoc, blockId: string): ScriptNodeLike | null {
  return walkBlocks(doc).find((b) => getBlockId(b.node) === blockId)?.node ?? null;
}

export function countDialogueLines(doc: ProseMirrorDoc): number {
  return walkBlocks(doc).filter((b) => getBlockStyle(b.node) === "dialogue").length;
}
